/**
 * useLeadStageDropdown
 *
 * Fetch function for the lead stage paginated dropdown, designed for use
 * with react-select-async-paginate + React Query caching.
 *
 * Data source: POST /global-dropdown?module=lead_stage
 * Optionally filtered by practiceAreaId / caseId, always with lead_stage_status=ACTIVE.
 *
 * Caching strategy:
 *   Each unique combination of (practiceAreaId, caseId, search, page, pageSize)
 *   is stored as a separate React Query entry. LeadStageSelect calls
 *   queryClient.fetchQuery() per page load, so:
 *   - First open: fetches from API, writes to cache.
 *   - Subsequent opens (within staleTime): served from cache instantly, no network request.
 *   - staleTime: 5 min  |  gcTime: 10 min
 */

import { getGlobalDropdown } from 'services/caseLeadDropdownService';

/**
 * Fetches one page of lead stages from the global dropdown API.
 *
 * @param {Object}      params
 * @param {string|null} params.practiceAreaId  - Filter by practice area (optional)
 * @param {string}      params.caseId          - Case context passed to the API
 * @param {string}      params.search          - Keyword search string
 * @param {number}      params.page            - 1-based page number
 * @param {number}      params.pageSize        - Records per page
 * @returns {Promise<{ options: Array, hasMore: boolean, additional: { page: number } }>}
 */
export const fetchLeadStagesPage = async ({ practiceAreaId = null, caseId = '', search = '', page = 1, pageSize = 15 }) => {
  const body = {
    practice_area_id: practiceAreaId ? [practiceAreaId] : [],
    show_unassigned_pa_column: true,
  };

  const response = await getGlobalDropdown(
    '',
    'lead_stage',
    search,
    caseId,
    true,
    page,
    pageSize,
    '',
    'ACTIVE',
    body,
    undefined,
    undefined,
    undefined,
    undefined,
    true
  );

  return {
    options: response?.data?.data || [],
    hasMore: response?.data?.has_next ?? false,
    additional: { page },
  };
};
